import { Link } from "react-router-dom";
import type { CaseCardData } from "@/components/feature/CaseCard";

export interface HeadingItem {
  id: string;
  text: string;
}

interface ArticleAsideProps {
  headings: HeadingItem[];
  tags: string[];
  related: CaseCardData[];
}

export default function ArticleAside({ headings, tags, related }: ArticleAsideProps) {
  return (
    <aside className="lg:sticky lg:top-[92px] space-y-6" aria-label="文章辅助信息">
      {headings.length > 0 && (
        <nav
          aria-label="本篇目录"
          className="p-5 rounded-lg border border-background-200 bg-background-50"
        >
          <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
            <i className="ri-list-check-2 text-primary-600" aria-hidden="true"></i>
            本篇目录
          </h2>
          <ol className="mt-4 space-y-2.5">
            {headings.map((item, index) => (
              <li key={item.id} className="flex gap-2.5">
                <span className="text-xs font-label text-accent-700 pt-0.5">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <a
                  href={`#${item.id}`}
                  className="text-sm leading-snug text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
                >
                  {item.text}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="p-5 rounded-lg border border-background-200 bg-background-50">
        <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
          <i className="ri-price-tag-3-line text-secondary-600" aria-hidden="true"></i>
          关键词
        </h2>
        <ul className="mt-3 flex flex-wrap gap-2">
          {tags.map((tag) => (
            <li key={tag}>
              <span className="inline-block px-2.5 py-1 rounded-full text-xs bg-secondary-100 text-secondary-900">
                {tag}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="p-5 rounded-lg border border-primary-200 bg-primary-50">
        <h2 className="font-heading text-sm font-bold text-primary-900 flex items-center gap-2">
          <i className="ri-shield-star-line" aria-hidden="true"></i>
          正在经历网暴？
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-primary-900/80">
          先取证、再止损、最后维权。我们把可以照着做的步骤整理成了一份清单。
        </p>
        <Link
          to="/cases/after-the-storm"
          className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap"
        >
          查看自救与取证清单
          <i className="ri-arrow-right-line" aria-hidden="true"></i>
        </Link>
      </div>

      {related.length > 0 && (
        <div className="p-5 rounded-lg border border-background-200 bg-background-50">
          <h2 className="font-heading text-sm font-bold text-foreground-950 flex items-center gap-2">
            <i className="ri-links-line text-secondary-600" aria-hidden="true"></i>
            相关档案
          </h2>
          <ul className="mt-4 space-y-4">
            {related.map((item) => (
              <li key={item.slug}>
                <Link
                  to={`/cases/${item.slug}`}
                  className="group flex gap-3 cursor-pointer"
                >
                  <span className="w-16 h-14 shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={item.cover}
                      alt={`${item.title} 配图`}
                      title={`${item.title} · 网暴黑料调查`}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-heading text-sm font-semibold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {item.title}
                    </span>
                    <span className="mt-1 block text-xs text-foreground-500">{item.readTime}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}